import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Milestone, 
  Star, 
  Lightbulb, 
  Heart, 
  Target, 
  Sparkles,
  Trophy
} from "lucide-react";
import { format } from "date-fns";

interface ClientMilestonesViewProps {
  clientId: string;
  clinicianId: string;
}

const MILESTONE_ICONS: Record<string, any> = {
  breakthrough: Lightbulb,
  skill_acquisition: Target,
  emotional_shift: Heart,
  behaviour_change: Sparkles,
  goal_reached: Trophy
};

export function ClientMilestonesView({ clientId, clinicianId }: ClientMilestonesViewProps) {
  const { data: milestones, isLoading } = useQuery({
    queryKey: ['client-milestones-view', clientId, clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_journey_milestones')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', clinicianId)
        .eq('shared_with_client', true)
        .order('milestone_date', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getMilestoneIcon = (type: string) => {
    const Icon = MILESTONE_ICONS[type] || Milestone;
    return <Icon className="h-5 w-5" />;
  };

  const getMilestoneColor = (type: string) => {
    switch (type) {
      case 'breakthrough': return 'text-yellow-500 bg-yellow-500/10';
      case 'skill_acquisition': return 'text-blue-500 bg-blue-500/10';
      case 'emotional_shift': return 'text-pink-500 bg-pink-500/10';
      case 'behaviour_change': return 'text-green-500 bg-green-500/10';
      case 'goal_reached': return 'text-purple-500 bg-purple-500/10';
      default: return 'text-primary bg-primary/10';
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'}`}
      />
    ));
  };

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          My Journey Milestones
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!milestones || milestones.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Trophy className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium text-foreground mb-2">Your Journey Awaits</h3>
            <p className="text-sm max-w-sm mx-auto">
              As you progress in your therapeutic journey, your milestones and achievements will appear here.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border/50" />
            
            <div className="space-y-6">
              {milestones.map((milestone, index) => (
                <div key={milestone.id} className="relative flex gap-4">
                  {/* Icon */}
                  <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${getMilestoneColor(milestone.milestone_type)}`}>
                    {getMilestoneIcon(milestone.milestone_type)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-foreground">{milestone.title}</h4>
                        <span className="text-xs text-muted-foreground">
                          {milestone.milestone_date && format(new Date(milestone.milestone_date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      
                      {milestone.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {milestone.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="capitalize">
                          {milestone.milestone_type.replace('_', ' ')}
                        </Badge>
                        {milestone.significance_rating && (
                          <div className="flex items-center gap-1">
                            {renderStars(milestone.significance_rating)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
